import React, { useEffect, useRef, useState } from 'react';
import { Container, ContentContainer, GraphContainer, TabContainer, DateFilter } from './styles';

import androidIcon from "../../assets/icons/dashboard/android.svg";
import appleIcon from "../../assets/icons/dashboard/apple.svg";
import chromeIcon from "../../assets/icons/dashboard/chrome.svg";
import desktopIcon from "../../assets/icons/dashboard/desktop.svg";
import edgeIcon from "../../assets/icons/dashboard/edge.svg";
import firefoxIcon from "../../assets/icons/dashboard/firefox.svg";
import mobileIcon from "../../assets/icons/dashboard/mobile.svg";
import spinnerIcon from "../../assets/icons/dashboard/spinner.svg";
import tabletIcon from "../../assets/icons/dashboard/tablet.svg";
import windowsIcon from "../../assets/icons/dashboard/windows.svg";
import linuxIcon from "../../assets/icons/dashboard/linux.svg";
import saveIcon from "../../assets/icons/save-icon.svg";
import operaIcon from "../../assets/icons/dashboard/opera.svg";

import Chart from 'chart.js/auto';
import {
    getAcessCountGraph, getScenarioCountGraph, getLogTypeCountGraph, getOSCountGraph, getBrowserCountGraph, getDeviceTypeCountGraph
} from './serviceApi';
import { useNavigate } from 'react-router-dom';
import { PanelHeader } from '../../components/PanelHeader';
import { getLoggedUser } from '../../service/helpCaseService';

const LoadingSpinner = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', width: '100%', flexDirection: 'column', color: '#123c5f' }}>
        <style>
            {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
        </style>
        <img
            src={spinnerIcon}
            alt="Loading"
            style={{ width: '40px', height: '40px', animation: 'spin 1s linear infinite' }}
        />
        <p style={{ marginTop: '10px', fontWeight: 'bold' }}>Carregando dados...</p>
    </div>
);

const downloadBtnStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#123c5f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '10px',
    marginLeft: 'auto'
};

const Dashboard = () => {
    // Refs
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const chartInstance = useRef<Chart | null>(null);
    const featureCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const featureChartInstance = useRef<Chart | null>(null);
    const scenarioCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const scenarioChartInstance = useRef<Chart | null>(null);
    const osCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const osChartInstance = useRef<Chart | null>(null);
    const browserCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const browserChartInstance = useRef<Chart | null>(null);
    const deviceCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const deviceChartInstance = useRef<Chart | null>(null);

    // State
    const [tabNumber, setTabNumber] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [accessData, setAccessData] = useState<{ date: string; accesses: number }[]>([]);
    const [scenarioUsage, setScenarioUsage] = useState<{ name: string; accessCount: number }[]>([]);
    const [featureUsage, setFeatureUsage] = useState<{ name: string; accessCount: number }[]>([]);
    const [operatingSystems, setOperatingSystems] = useState<{ name: string; key: string; accessCount: number }[]>([]);
    const [browserUsage, setBrowserUsage] = useState<{ name: string; key: string; accessCount: number }[]>([]);
    const [deviceTypes, setDeviceTypes] = useState<{ name: string; key: string; accessCount: number }[]>([]);
    
    // Dates
    const tempEndDate = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const [endDate, setEndDate] = useState(tempEndDate);
    const [startDate, setStartDate] = useState<string>(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    const minDate = "2026-01-01";

    const navigate = useNavigate();

    const FEATURE_NAME_MAP: Record<string, string> = {
        "DASHBOARD_LIST_NEW_DEVICES": "Lista de novos dispositivos",
        "DASHBOARD_DATE_ACCESSES": "Acessos por data",
        "DASHBOARD_NEW_DEVICE_COUNT": "Total de novos dispositivos",
        "DASHBOARD_NEW_DEVICE_OS_COUNT": "novos SOs",
        "DASHBOARD_NEW_DEVICE_TYPE_COUNT": "novos dispositivos",
        "DASHBOARD_NEW_DEVICE_CLIENT_COUNT": "novos navegadores",
        "DASHBOARD_SCENARIO_COUNT": "Acessos por conteúdo",
        "DASHBOARD_LOG_TYPE_COUNT": "Acessos por tipo de log",
        "DEVICE_START": "Acesso ao portal",
        "SESSION_START": "Início de sessão",
        "FINISH_SESSION": "Fim de sessão",
        "CREATE_FEEDBACK": "Envio de feedback",
        "LIST_FEEDBACK": "Lista de feedbacks",
        "DELETE_FEEDBACK": "Exclusão de feedback",
        "GET_FEEDBACK_FILE_PRESIGNED_URL": "Visualização de vídeo (Feedback)",
        "SEARCH_GLOSSARY_BY_ITEM": "Busca no glossário",
        "SEARCH_GLOSSARY_BY_TOKENS": "Acionamento de Libras",
        "CREATE_HELP_CASE": "Solicitação de ajuda",
        "ANSWER_HELP_CASE": "Resposta de ajuda",
        "LIST_HELP_CASES": "Lista de solicitações de ajuda",
        "GET_HELP_CASE_FILE_PRESIGNED_URL": "Visualização de vídeo (Ajuda)",
        "GET_QUESTIONS_BY_SCENARIO": "Visualização de itens de conteúdo",
        "CREATE_SCENARIO": "Criação de conteúdo",
        "UPDATE_SCENARIO": "Atualização de conteúdo",
        "LIST_SCENARIO_DETAILS": "Detalhes do conteúdo",
        "LIST_SCENARIOS": "Lista de conteúdos",
        "DELETE_SCENARIO": "Exclusão de conteúdo",
        "SUGGEST_SCENARIO": "Sugestão de conteúdo",
        "SEND_SCENARIO_BY_EMAIL": "Envio de conteúdo por e-mail",
        "VACATION_GET_USER_DATA": "Consulta de Dados de Férias",
        "VACATION_REQUEST_HOLIDAY": "Solicitação de Férias",
    };

    const ICONS = {
        WINDOWS: <img className='icon' src={windowsIcon} alt="Windows" />,
        ANDROID: <img className='icon' src={androidIcon} alt="Android" />,
        CHROME: <img className='icon' src={chromeIcon} alt="Chrome" />,
        DESKTOP: <img className='icon' src={desktopIcon} alt="Desktop" />,
        MOBILE: <img className='icon' src={mobileIcon} alt="Mobile" />,
        IOS: <img className='icon' src={appleIcon} alt="iOS" />,
        MS_EDGE: <img className='icon' src={edgeIcon} alt="Edge" />,
        FIREFOX: <img className='icon' src={firefoxIcon} alt="Firefox" />,
        OPERA: <img className='icon' src={operaIcon} alt="Opera" />,
        TABLET: <img className='icon' src={tabletIcon} alt="Tablet" />,
        LINUX: <img className='icon' src={linuxIcon} alt="Linux" />
    };

    const totalAccesses = accessData.reduce((total, item) => total + item.accesses, 0);

    const transformDate = (dateString: string): string => {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}`;
    };

    const validateMaxRange = (start: string, end: string) => {
        const d1 = new Date(start);
        const d2 = new Date(end);
        const diffInTime = d2.getTime() - d1.getTime();
        const diffInDays = diffInTime / (1000 * 3600 * 24);
        return diffInDays < 30;
    };

   const downloadChart = (
        ref: React.RefObject<HTMLCanvasElement>, 
        fileName: string, 
        chartInstanceRef?: React.MutableRefObject<Chart | null>
    ) => {
        if (ref.current) {
            if (chartInstanceRef && chartInstanceRef.current) {
                const chart = chartInstanceRef.current;
                
                const originalLabels = [...chart.data.labels as string[]];
                const originalDisplay = chart.options.plugins?.legend?.display;
                const originalPosition = chart.options.plugins?.legend?.position;

                const values = chart.data.datasets[0].data;
                chart.data.labels = originalLabels.map((label, i) => `${label}: ${values[i]}`);

                if (chart.options.plugins) {
                    if (!chart.options.plugins.legend) {
                        chart.options.plugins.legend = {};
                    }
                    chart.options.plugins.legend.display = true;
                    chart.options.plugins.legend.position = 'bottom';
                }

                chart.update('none');

                const link = document.createElement('a');
                link.download = `${fileName}_${startDate}_${endDate}.png`;
                link.href = ref.current.toDataURL('image/png');
                link.click();

                chart.data.labels = originalLabels;
                if (chart.options.plugins?.legend) {
                    chart.options.plugins.legend.display = originalDisplay;
                    chart.options.plugins.legend.position = originalPosition;
                }
                chart.update('none');
            } else {
                const link = document.createElement('a');
                link.download = `${fileName}_${startDate}_${endDate}.png`;
                link.href = ref.current.toDataURL('image/png');
                link.click();
            }
        }
    };

    const renderCustomLegend = (data: any[], colors: string[]) => {
        const activeData = data.filter(i => i.accessCount > 0);
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px', marginTop: '15px' }}>
                {activeData.map((item, index) => (
                    <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#444' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: colors[index % colors.length] }} />
                        <span style={{ display: 'flex', fontSize: '16px', color: '#123c5f' }}>
                            {ICONS[item.key as keyof typeof ICONS] || null}
                        </span>
                        <span><strong>{item.name}</strong> ({item.accessCount})</span>
                    </div>
                ))}
            </div>
        );
    };

    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newStart = e.target.value;
        if (newStart > endDate) {
            setStartDate(newStart);
            setEndDate(newStart);
            return;
        }
        if (validateMaxRange(newStart, endDate)) {
            setStartDate(newStart);
        } else {
            setEndDate(newStart);
            alert("O intervalo máximo permitido é de 30 dias.");
        }
    };

    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newEnd = e.target.value;
        if (newEnd >= startDate) {
            if (validateMaxRange(startDate, newEnd)) {
                setEndDate(newEnd);
            } else {
                alert("O intervalo máximo permitido é de 30 dias.");
            }
        }
    };

    useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        if (await getLoggedUser()) {
          await Promise.all([
            getAcessCountGraph(startDate, endDate, () => { }, setAccessData),
            getScenarioCountGraph(startDate, endDate, setScenarioUsage),
            getLogTypeCountGraph(startDate, endDate, (data: any) => {
              const formatted = data.map((item: any) => ({
                name: FEATURE_NAME_MAP[item.key] || item.key,
                accessCount: item.accessCount
              }));
              setFeatureUsage(formatted);
            }),
            getOSCountGraph(startDate, endDate, setOperatingSystems),
            getBrowserCountGraph(startDate, endDate, setBrowserUsage),
            getDeviceTypeCountGraph(startDate, endDate, setDeviceTypes)
          ]);
          setIsLoading(false);
        }
      } catch (error) {
        navigate("/login");
      }
    };
    
    fetchData();
  }, [startDate, endDate]);

    // Graph Effects (Tab 0)
    useEffect(() => {
        if (tabNumber === 0 && canvasRef.current && !isLoading) {
            const ctx = canvasRef.current.getContext('2d');
            if (ctx) {
                if (chartInstance.current) chartInstance.current.destroy();

                chartInstance.current = new Chart(ctx, {
                    type: 'line',
                    data: {
                        labels: accessData.map(item => transformDate(item.date)),
                        datasets: [{
                            label: 'Acessos por Dia',
                            data: accessData.map(item => item.accesses),
                            borderColor: 'rgb(75, 192, 192)',
                            backgroundColor: 'rgba(75, 192, 192, 0.2)',
                            tension: 0.3,
                            fill: true
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: { ticks: { minRotation: 45, maxRotation: 45 } },
                            y: { beginAtZero: true, ticks: { stepSize: 1, precision: 0 } }
                        },
                        plugins: {
                            legend: { position: 'top' },
                            title: { display: true, text: 'Histórico de Acessos' }
                        }
                    }
                });
            }
        }
        return () => { if (chartInstance.current) chartInstance.current.destroy(); };
    }, [tabNumber, accessData, isLoading]);

    // Graph Effects (Tab 1)
    useEffect(() => {
        if (tabNumber === 1 && !isLoading) {
            if (featureCanvasRef.current) {
                const ctx1 = featureCanvasRef.current.getContext('2d');
                if (ctx1) {
                    if (featureChartInstance.current) featureChartInstance.current.destroy();
                    const activeFeatures = featureUsage
                        .filter(i => i.accessCount > 0)
                        .sort((a, b) => b.accessCount - a.accessCount);
                    featureChartInstance.current = new Chart(ctx1, {
                        type: 'bar',
                        data: {
                            labels: activeFeatures.map(i => i.name),
                            datasets: [{ label: 'Acessos', data: activeFeatures.map(i => i.accessCount), backgroundColor: '#36A2EB', borderRadius: 5 }]
                        },
                        options: {
                            indexAxis: 'y', responsive: true, maintainAspectRatio: false,
                            plugins: { title: { display: true, text: 'Uso de Funcionalidades' }, legend: { display: false } },
                            scales: { x: { beginAtZero: true, ticks: { stepSize: 2, precision: 0 } } }
                        }
                    });
                }
            }
            if (scenarioCanvasRef.current) {
                const ctx2 = scenarioCanvasRef.current.getContext('2d');
                if (ctx2) {
                    if (scenarioChartInstance.current) scenarioChartInstance.current.destroy();
                    const activeScenarios = scenarioUsage.filter(i => i.accessCount > 0).sort((a, b) => b.accessCount - a.accessCount);
                    scenarioChartInstance.current = new Chart(ctx2, {
                        type: 'bar',
                        data: {
                            labels: activeScenarios.map(i => i.name),
                            datasets: [{ label: 'Buscas', data: activeScenarios.map(i => i.accessCount), backgroundColor: '#123c5f', borderRadius: 5 }]
                        },
                        options: {
                            indexAxis: 'y', responsive: true, maintainAspectRatio: false,
                            plugins: { title: { display: true, text: 'Assuntos Mais Buscados' }, legend: { display: false } },
                            scales: { x: { beginAtZero: true, ticks: { stepSize: 2, precision: 0 } } }
                        }
                    });
                }
            }
        }
        return () => {
            if (featureChartInstance.current) featureChartInstance.current.destroy();
            if (scenarioChartInstance.current) scenarioChartInstance.current.destroy();
        };
    }, [tabNumber, isLoading, scenarioUsage, featureUsage]);

    // Graph Effects (Tab 2)
    useEffect(() => {
        const pieColors = ['#36A2EB', '#45e768', '#FFCE56', '#FF6384', '#9966FF', '#FF9F40', '#4BC0C0', '#C9CBCF', '#FFCD56', '#36A2EB'];
        if (tabNumber === 2 && !isLoading) {
            const renderPie = (canvasRef: any, instanceRef: any, data: any[], title: string) => {
                if (canvasRef.current) {
                    const ctx = canvasRef.current.getContext('2d');
                    if (ctx) {
                        if (instanceRef.current) instanceRef.current.destroy();
                        const activeData = data.filter(i => i.accessCount > 0);
                        instanceRef.current = new Chart(ctx, {
                            type: 'pie',
                            data: {
                                labels: activeData.map(i => i.name),
                                datasets: [{ data: activeData.map(i => i.accessCount), backgroundColor: pieColors, hoverOffset: 4 }]
                            },
                            options: {
                                responsive: true, maintainAspectRatio: false,
                                plugins: { title: { display: true, text: title }, legend: { display: false } },
                            }
                        });
                    }
                }
            };
            renderPie(osCanvasRef, osChartInstance, operatingSystems, 'Sistemas Operacionais');
            renderPie(browserCanvasRef, browserChartInstance, browserUsage, 'Navegadores');
            renderPie(deviceCanvasRef, deviceChartInstance, deviceTypes, 'Tipos de Dispositivo');
        }
        return () => {
            osChartInstance.current?.destroy();
            browserChartInstance.current?.destroy();
            deviceChartInstance.current?.destroy();
        };
    }, [tabNumber, isLoading, operatingSystems, browserUsage, deviceTypes]);

    return (
        <Container>
            <PanelHeader title="Dashboard de acessos" />

            <TabContainer>
                <button className={tabNumber === 0 ? 'active' : ''} onClick={() => setTabNumber(0)}>Contabilizador de acessos</button>
                <button className={tabNumber === 1 ? 'active' : ''} onClick={() => setTabNumber(1)}>Detalhes de acessos</button>
                <button className={tabNumber === 2 ? 'active' : ''} onClick={() => setTabNumber(2)}>Detalhes de dispositivos</button>
            </TabContainer>

            <DateFilter>
                <div>
                    <label style={{ marginRight: '10px' }}>Início:</label>
                    <input type="date" value={startDate} min={minDate} max={tempEndDate} onChange={handleStartDateChange} />
                </div>
                <div>
                    <label style={{ marginRight: '10px' }}>Fim:</label>
                    <input type="date" value={endDate} min={minDate} max={tempEndDate} onChange={handleEndDateChange} />
                </div>
                <small>
                    {Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1} dias selecionados
                </small>
                <small>
                    Total de acessos: <strong>{totalAccesses}</strong>
                </small>
            </DateFilter>

            <ContentContainer>
                {isLoading ? (
                    <LoadingSpinner />
                ) : (
                    <>
                        {/* TAB 0: HISTÓRICO DE ACESSOS */}
                        {tabNumber === 0 ? (
                            <GraphContainer style={{ flexDirection: 'column' }}>
                                <div style={{ width: '98%', display: 'flex', justifyContent: 'flex-end' }}>
                                    <button style={downloadBtnStyle} onClick={() => downloadChart(canvasRef, 'Grafico_Historico_Acessos')}>
                                            <img className="save-icon" src={saveIcon} alt="save icon" /> Salvar
                                    </button>
                                </div>
                                <div style={{ width: "98%", height: "400px" }}>
                                    <canvas ref={canvasRef}></canvas>
                                </div>
                            </GraphContainer>
                        ) : null}

                        {/* TAB 1: FUNCIONALIDADES E CENÁRIOS */}
                        {tabNumber === 1 ? (
                            <>
                                <GraphContainer style={{ flexDirection: 'column' }}>
                                    <div style={{ width: '90%', display: 'flex', justifyContent: 'flex-end' }}>
                                        <button style={downloadBtnStyle} onClick={() => downloadChart(featureCanvasRef, 'Grafico_Uso_Funcionalidades')}>
                                            <img className="save-icon" src={saveIcon} alt="save icon" /> Salvar
                                        </button>
                                    </div>
                                    <div style={{ height: '400px', width: '90%' }}><canvas ref={featureCanvasRef} /></div>
                                </GraphContainer>

                                <GraphContainer style={{ flexDirection: 'column' }}>
                                    <div style={{ width: '90%', display: 'flex', justifyContent: 'flex-end' }}>
                                        <button style={downloadBtnStyle} onClick={() => downloadChart(scenarioCanvasRef, 'Grafico_Assuntos_Buscados')}>
                                            <img className="save-icon" src={saveIcon} alt="save icon" /> Salvar
                                        </button>
                                    </div>
                                    <div style={{ height: '500px', width: '90%' }}><canvas ref={scenarioCanvasRef} /></div>
                                </GraphContainer>
                            </>
                        ) : null}

                        {/* TAB 2: DISPOSITIVOS, NAVEGADORES, SO */}
                        {tabNumber === 2 ? (
                            <>
                                <GraphContainer style={{ flexDirection: 'column' }}>
                                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                                        <button style={downloadBtnStyle} onClick={() => downloadChart(osCanvasRef, 'Grafico_Sistemas_Operacionais', osChartInstance)}>
                                            <img className="save-icon" src={saveIcon} alt="save icon" /> Salvar
                                        </button>
                                    </div>
                                    <div className="canvas-wrapper"><canvas ref={osCanvasRef} /></div>
                                    {renderCustomLegend(operatingSystems, ['#36A2EB', '#45e768', '#FFCE56', '#FF6384', '#9966FF'])}
                                </GraphContainer>

                                <GraphContainer style={{ flexDirection: 'column' }}>
                                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                                        {/* Passando browserChartInstance */}
                                        <button style={downloadBtnStyle} onClick={() => downloadChart(browserCanvasRef, 'Grafico_Navegadores', browserChartInstance)}>
                                            <img className="save-icon" src={saveIcon} alt="save icon" /> Salvar
                                        </button>
                                    </div>
                                    <div className="canvas-wrapper"><canvas ref={browserCanvasRef} /></div>
                                    {renderCustomLegend(browserUsage, ['#36A2EB', '#45e768', '#FFCE56', '#FF6384', '#9966FF'])}
                                </GraphContainer>

                                <GraphContainer style={{ flexDirection: 'column' }}>
                                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                                        <button style={downloadBtnStyle} onClick={() => downloadChart(deviceCanvasRef, 'Grafico_Tipos_Dispositivo', deviceChartInstance)}>
                                            <img className="save-icon" src={saveIcon} alt="save icon" /> Salvar
                                        </button>
                                    </div>
                                    <div className="canvas-wrapper"><canvas ref={deviceCanvasRef} /></div>
                                    {renderCustomLegend(deviceTypes, ['#36A2EB', '#45e768', '#FFCE56', '#FF6384', '#9966FF'])}
                                </GraphContainer>
                            </>
                        ) : null}
                    </>
                )}
            </ContentContainer>
        </Container>
    );
}

export default Dashboard;